import { Injectable } from "@angular/core";
import * as appConfig from "@config";

type KeyValues<T> = T extends string ? T : { [K in keyof T]: KeyValues<T[K]> }[keyof T];

export const MESSAGE_TYPES = {
	// events sent by v4 to v3
	TIN_IFRAME_CLOSED: "TIN_IFRAME_CLOSED",
	TIN_IFRAME_FORM_SUBMIT_SUCCESS: "TIN_IFRAME_FORM_SUBMIT_SUCCESS",
	TIN_IFRAME_FORM_SUBMIT_ERROR: "TIN_IFRAME_FORM_SUBMIT_ERROR",
	TIN_IFRAME_COUNTRY_REMOVED: "TIN_IFRAME_COUNTRY_REMOVED",
	TIN_IFRAME_CS_HELP_CLICKED: "TIN_IFRAME_CS_HELP_CLICKED",
	// events sent by v3 to v4
	V3_READY: "V3_READY",
	ACTION_SUCCESS: "ACTION_SUCCESS",
	ACTION_ERROR: "ACTION_ERROR",
	IFRAME_HEIGHT: "IFRAME_HEIGHT",
	MODAL_CLOSE: "MODAL_CLOSE"
} as const;

export type MessageType = KeyValues<typeof MESSAGE_TYPES>;

export const NOTIFICATION_STATES = {
	ERROR: "error",
	INFORMATION: "information",
	SUCCESS: "success"
} as const;

export type NotificationState = (typeof NOTIFICATION_STATES)[keyof typeof NOTIFICATION_STATES];

export interface Message {
	type: MessageType;
	notification?: INotificationMessage;
	countryIso?: string;
	height?: number;
}

export interface INotificationMessage {
	state: NotificationState;
	subtitle?: string;
	title?: string;
}

@Injectable({
	providedIn: "root"
})
export class IframeV4EventsHandlers {
	private targetOrigin = appConfig.config.V4AppBaseUrl;
	private messageHandler: ((event: MessageEvent) => void) | null = null;

	/**
	 * Set up a message event listener
	 * @param handler The message event handler function
	 */
	onMessageReceived(handler: (event: MessageEvent) => void): void {
		this.messageHandler = (ev: MessageEvent) => {
			handler(ev);
		};

		window?.addEventListener("message", this.messageHandler);
	}

	/**
	 * Remove a message event listener
	 * @param handler The message event handler function to remove
	 */
	dispose(): void {
		if (this.messageHandler) {
			window?.removeEventListener("message", this.messageHandler);
			this.messageHandler = null;
			console.info("Message event listener removed");
		}
	}

	notifyModalClose(): void {
		if (window !== window.parent) {
			this.postMessage({ type: MESSAGE_TYPES.MODAL_CLOSE });
		}
	}

	/**
	 * Post a message to the parent window
	 * @param message
	 * @param targetOrigin
	 */
	postMessage(message?: Message, targetOrigin?: string): void {
		if (!targetOrigin && !this.targetOrigin) {
			throw new Error('IframeHandlerV2: "targetOrigin" is not defined');
		}

		console.info("Posting message to parent window:", message);
		window?.parent.postMessage(message, targetOrigin ?? this.targetOrigin);
	}
}
